import CloseIcon from '@mui/icons-material/Close'
import { Grid2, styled, Tooltip, useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useFormik } from 'formik'
import {
    filter,
    find,
    findIndex,
    flattenDeep,
    has,
    includes,
    indexOf,
    isUndefined,
    values as lodashvalues,
    map,
    throttle,
    uniq,
} from 'lodash'
import { forwardRef, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import WaveSurfer from 'wavesurfer.js'
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js'
import { useInvalidateThreadListQuery } from '../../api/hooks/useInvalidateThreadListQuery'
import { useInvalidateWorkplaceListQuery } from '../../api/hooks/useInvalidateWorkplaceListQuery'
import AssistantLogo from '../../assets/icons/AssistantLogo.svg?react'
import MicrophoneIcon from '../../assets/icons/Microphone.svg?react'
import PauseIcon from '../../assets/icons/Pause.svg?react'
import PenSquare from '../../assets/icons/PenSquare.svg?react'
import PlayIcon from '../../assets/icons/Play.svg?react'
import SquareIcon from '../../assets/icons/Square.svg?react'
import TrashIcon from '../../assets/icons/Trash.svg?react'
import { REGULAR_REQUESTS_LIMIT_FOR_THREAD } from '../../constants/regularRequests.constants'
import { FileListDialog } from '../../dialogs/FileListDialog/FileListDialog'
import {
    RegularRequestCreateDialog,
    RegularRequestCreateDialogData,
} from '../../dialogs/RegularRequestCreateDialog'
import { usePackageSubscription } from '../../hooks/usePackageSubscription'
import { usePersonalDiscount } from '../../hooks/usePersonalDiscount'
import { AgentOrAssistantSelect } from '../../pages/InternalPage/MainPage/components/AgentOrAssistantSelect'
import { validateFiles } from '../../pages/InternalPage/RequestSettingPage/components/filesInRequest'
import { FilesMenu } from '../../pages/InternalPage/RequestSettingPage/components/FilesMenu'
import { useFilesStore } from '../../store/storeFiles'
import { useProfileStore } from '../../store/storeProfile'
import { useRequestsStore } from '../../store/storeRequests'
import { useTextSelectionMenuActions } from '../../store/storeTextSelectionMenu'
import { useWrapperStore } from '../../store/storeWrapper'
import { theme } from '../../theme'
import { getFileType } from '../../utils/files'
import { DocumentIconName } from '../DocumentIconName'
import { Notistack } from '../Notistack'
import { RequestSettingButton } from '../RequestSettingButton'
import { WebSearchButton } from '../WebSearchButton'
import { OnboardingTooltip } from '../Wrapper/NavigationSidebar/components/OnboardingTooltip'
import { ProjectSelect } from './ProjectSelect'
import { Quote } from './Quote'
import { ReplyCost } from './ReplyCost'
import { RequestBox } from './RequestInput.styles'
import { SubmitButton } from './SubmitButton'

export const mapContentType = {
    IMAGE: 'Изображений',
    VIDEO: 'Видео',
    AUDIO_VOICE: 'Записей',
    AUDIO: 'Аудиофайлов',
    DOCUMENT: 'Документов',
}

const RequestInputWrapper = styled(Grid2, {
    shouldForwardProp: (propName: string) => !['isMainPage', 'isTempUser'].includes(propName),
})<{
    isMainPage: boolean
    isTempUser: boolean
}>(({ theme, isMainPage, isTempUser, isMobile, stepOnboarding }) => ({
    bottom: !isMainPage && isTempUser ? '16px' : '0',
    position: 'relative',
    background: 'transparent',
    width: isMainPage ? '80%' : '100%',
    minHeight: 'auto',
    right: '0',
    padding: !isMainPage ? '0 16px' : '',
    marginTop: !isMainPage ? '16px' : 0,
    [theme.breakpoints.down('md')]: {
        width: `100%`,
        padding: '0 16px',
    },
    [theme.breakpoints.down('sm')]: {
        width: `100%`,
        padding: '0',
    },
    zIndex: !isMobile && stepOnboarding === 5 ? 1304 : 'auto',
    // pointerEvents: stepOnboarding === 100 ? 'auto' : 'none',
}))

const FilesWrapper = styled(Grid2)(({ theme }) => ({
    padding: '12px 16px',
    gap: '4px',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}))

export const getAvailableFileDetails = (supported_content, type) => {
    return find(supported_content, (content) => {
        return content.is_supported && includes(content.available_extensions, type)
    })
}

const StyledInput = styled(TextField)(() => ({
    '& .MuiInputBase-input::placeholder': {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    '& .MuiInputBase-root': {
        padding: '0',
        marginBottom: '16px',
        alignItems: 'center',
    },
}))

type Props = {
    isMainPage?: boolean
    message?: string
    assistantPresets?: string[]
    isArchivedRequest?: boolean
    suggestedRequest?: string
    helpButton?: ReactNode
    onSendMessage?: VoidFunction
    disabled?: boolean
}

export const RequestInput = forwardRef<HTMLDivElement, Props>(
    (
        {
            isMainPage = false,
            message,
            assistantPresets,
            suggestedRequest = '',
            isArchivedRequest,
            helpButton,
            onSendMessage,
            disabled,
        },
        ref,
    ) => {
        const navigate = useNavigate()

        const inputRef = useRef('')
        const {
            projectListActive,
            modelListProtected,
            currentAgentNameInDialog,
            currentAssistantIdDialog,
            setStateNotification,
            connectAssistantId,
            uploadFiles,
            modelDetail,
            isScrollToRequestInput,
            isLoadingUploadFiles,
            isWebSearch,
            isTempUser,
            fetchReplyCost,
            replyCost,
            isProtectedCurrentProject,
            setIsProtectedCurrentProject,
            setCurrentAgentAndAssistantIdDialog,
            stepOnboarding,
            requestByEdit,
            setRequestByEdit,
        } = useWrapperStore((state) => state)

        const {
            createRequest,
            createRequestMessage,
            errorCreateRequestMessage,
            errorCreateRequest,
            requestSettingAdditionalContexts,
            requestSettingParams,
            requestMemoryMessageCount,
            requestSettingRagEnabled,
            requestSettingVoiceAnswer,
            requestSettingContext,
            requestSettingLlmTools,
            filesInRequest,
            setFilesInRequest,
            setIsCanFilesInRequest,
            files,
            setFile,
            isLoadingCreateRequestMessage,
            requestDetail,
            updateRegularRequestsCountInRequestDetail,
            maxFileCountByType,
            setMaxFileCountByType,
            filesError,
            setFilesError,
            availableExtensionsIsRequired,
            setAvailableExtensionsIsRequired,
            regenerateRequestMessage,
        } = useRequestsStore((state) => state)

        const { fileUrlList, setFileUrlList, isDoneChoiceFileUrl, setChoiceFileUrl } =
            useFilesStore((state) => state)
        const { isFullAccess, balance, userTariff } = useProfileStore()
        const { quoteClear, addQuoteInRequestText } = useTextSelectionMenuActions()

        const { promotePersonalDiscount } = usePersonalDiscount()
        const { promoteUpdatePackageSubscription } = usePackageSubscription()

        const [searchParams] = useSearchParams()
        const params = useParams()
        const [audioFile, setAudioFile] = useState<File | null>(null)
        const [fileTypes, setFileTypes] = useState<string[]>([])
        const [openFileListDialog, setOpenFileListDialog] = useState<boolean>(false)
        const [refrehAvailableParamsFiles, setRefrehAvailableParamsFiles] = useState<boolean>(false)
        const [availableExtensionsByType, setAvailableExtensionsByType] = useState<object>(null)
        const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
        const waveformRef = useRef<HTMLDivElement>(null)
        const wavesurferRef = useRef<WaveSurfer | null>(null)
        const recordRef = useRef<InstanceType<typeof RecordPlugin> | null>(null)
        const [isPlaying, setIsPlaying] = useState(false)

        const [isRecording, setIsRecording] = useState(false)
        const [recordTime, setRecordTime] = useState(0)
        const [createRegularRequestError, setCreateRegularRequestError] = useState(false)

        const [regularRequestDialogData, setRegularRequestDialogData] =
            useState<RegularRequestCreateDialogData | null>(null)

        const invalidateThreadListQuery = useInvalidateThreadListQuery()
        const invalidateWorkplaceListQuery = useInvalidateWorkplaceListQuery()

        const workplaceId = useMemo(() => {
            const workplaceId = searchParams.get('workplaceId')

            return workplaceId && find(projectListActive, { id: workplaceId })
                ? workplaceId
                : undefined
        }, [searchParams, projectListActive])

        const currentThreadId = params.requestId ? +params.requestId : undefined

        const [isSubmittingInProgress, setIsSubmittingInProgress] = useState(false)

        const {
            handleSubmit,
            values,
            touched,
            handleBlur,
            handleChange,
            errors,
            isValid,
            dirty,
            setValues,
            resetForm,
            setFieldValue,
            isSubmitting,
        } = useFormik({
            initialValues: {
                text: '',
                workplaceId: workplaceId,
                agent: currentAgentNameInDialog,
            },
            onSubmit: async (values, { resetForm }) => {
                if (isSubmittingInProgress) {
                    return
                }

                setIsSubmittingInProgress(true)

                try {
                    const { text: inputText } = values
                    if (files && lodashvalues(availableExtensionsIsRequired)[0]) {
                        setFilesError([
                            'Для корректной работы выбранной модели необходимо приложить файл в запросе',
                        ])
                        return
                    }

                    if (params.requestId) {
                        let filesUrl = undefined
                        let voice_url = null
                        if (files) {
                            filesUrl = await uploadFiles({ files }, params.requestId)
                        }

                        if (audioFile) {
                            const [url] = await uploadFiles(
                                { files: [audioFile] },
                                params.requestId,
                            )
                            voice_url = url
                        }

                        const text = addQuoteInRequestText(inputText || suggestedRequest)

                        await createRequestMessage(
                            {
                                thread_id: +params.requestId,
                                text,
                                image_url: null,
                                contexts: [],
                                files: filesUrl,
                                voice_url,
                            },
                            modelDetail?.is_free ? 0 : 1,
                        )

                        quoteClear()
                        setRefrehAvailableParamsFiles(true)
                        setFile([])
                        resetForm()
                        setAudioFile(null)
                        wavesurferRef.current?.empty()
                        onSendMessage?.()
                        invalidateThreadListQuery()
                        invalidateWorkplaceListQuery()
                    } else {
                        const name = audioFile
                            ? `Голосовое сообщение ${new Date().toISOString().substring(0, 10)}`
                            : inputText.length > 200
                              ? inputText.substring(0, 197) + '...'
                              : inputText

                        const threadData = await handleCreateThread(name)

                        if (threadData?.id) {
                            await createRequestNewAPI(threadData.id)
                        }
                    }

                    promotePersonalDiscount()
                    promoteUpdatePackageSubscription()
                } finally {
                    setIsSubmittingInProgress(false)
                }
            },
        })

        useEffect(() => {
            const currentProject = find(projectListActive, { id: values.workplaceId })

            setIsProtectedCurrentProject(
                isUndefined(currentProject) ? false : currentProject.is_protected,
            )
        }, [values.workplaceId])

        useEffect(() => {
            if (params.requestId && requestDetail) {
                const currentProject = find(projectListActive, { id: requestDetail.workplace_id })
                setIsProtectedCurrentProject(
                    isUndefined(currentProject) ? false : currentProject.is_protected,
                )
            }
        }, [params.requestId, requestDetail])

        useEffect(() => {
            if (!isUndefined(balance) && isUndefined(currentThreadId)) {
                const searchParamAssistantId = searchParams.get('assistant_id')
                const assistantId = searchParamAssistantId ? +searchParamAssistantId : null
                const currentAgentNameInDialog = isProtectedCurrentProject
                    ? modelListProtected[0].slug
                    : isFullAccess
                      ? 'AUTO'
                      : userTariff?.tariff?.restrictions?.available_models[0]
                setCurrentAgentAndAssistantIdDialog({
                    agent: currentAgentNameInDialog,
                    assistantId: assistantId ?? undefined,
                })
            }
        }, [isProtectedCurrentProject, balance, isFullAccess])

        useEffect(() => {
            if (!waveformRef.current) return

            const ws = WaveSurfer.create({
                container: waveformRef.current,
                height: 23,
                waveColor: '#BDBFC7',
                progressColor: '#006CFD',
                barWidth: 2,
                barGap: 4,
                cursorWidth: 0,
            })

            const record = ws.registerPlugin(
                RecordPlugin.create({
                    mimeType: 'audio/webm',
                    audioBitsPerSecond: 128000,
                }),
            )

            wavesurferRef.current = ws
            recordRef.current = record

            record.on('record-start', () => {
                setIsRecording(true)

                setRecordTime(0)
            })

            record.on('record-end', (blob: Blob) => {
                const audioFile = new File([blob], `recording_${new Date().toISOString()}.webm`, {
                    type: blob.type,
                })

                setAudioFile(audioFile)
                setIsRecording(false)
            })

            ws.on('play', () => setIsPlaying(true))
            ws.on('pause', () => setIsPlaying(false))
            ws.on('finish', () => setIsPlaying(false))

            return () => {
                ws.destroy()
            }
        }, [])

        useEffect(() => {
            let interval: NodeJS.Timeout
            if (isRecording) {
                interval = setInterval(() => {
                    setRecordTime((prev) => prev + 1)
                }, 1000)
            }
            return () => clearInterval(interval)
        }, [isRecording])

        const startRecording = throttle(
            async () => {
                try {
                    await recordRef.current?.startRecording()
                } catch (err) {
                    console.error('Ошибка доступа к микрофону:', err)
                    alert('Не удалось получить доступ к микрофону')
                }
            },
            2000,
            { trailing: false },
        )

        const stopRecording = () => {
            recordRef.current?.stopRecording()
            setIsRecording(false)
        }

        const formatTime = (seconds: number) => {
            const mins = Math.floor(seconds / 60)
            const secs = seconds % 60
            return `${mins}:${secs < 10 ? '0' : ''}${secs}`
        }

        const handleChangeFiles = async (filesSelected) => {
            setFilesError(null)
            const draftFiles = files ?? []

            const validationResult = validateFiles(
                [...draftFiles, ...filesSelected],
                modelDetail?.supported_content,
            )

            setFilesInRequest(undefined)
            setFile(validationResult.fileValidations)
            setFilesError(validationResult.isValid ? null : validationResult.errors)
        }

        const handleCloseFileListDialog = () => {
            setOpenFileListDialog(false)
        }

        useEffect(() => {
            if (filesInRequest) {
                handleChangeFiles(filesInRequest)
            }
        }, [filesInRequest])

        useEffect(() => {
            if (isScrollToRequestInput && inputRef?.current) {
                inputRef.current.focus()
            }
        }, [isScrollToRequestInput])

        useEffect(() => {
            setFile(undefined)
        }, [])

        useEffect(() => {
            if (modelDetail || refrehAvailableParamsFiles) {
                const maxFileCountByTypeOb = {}
                const availableExtensionsByTypeOb = {}
                const availableExtensionsIsRequiredOb = {}
                filter(modelDetail?.supported_content, {
                    is_supported: true,
                })?.map((item) => {
                    item.available_extensions.map((extension) => {
                        availableExtensionsByTypeOb[extension] = item.content_type
                    })

                    if (item.is_required) {
                        availableExtensionsIsRequiredOb[item.content_type] = true
                    }

                    maxFileCountByTypeOb[item.content_type] = item.max_file_count
                })
                setMaxFileCountByType(maxFileCountByTypeOb)
                setAvailableExtensionsByType(availableExtensionsByTypeOb)
                setAvailableExtensionsIsRequired(availableExtensionsIsRequiredOb)
                setRefrehAvailableParamsFiles(false)
            }
        }, [modelDetail, refrehAvailableParamsFiles])

        useEffect(() => {
            if (filesError?.length) {
                const errorText = map(filesError, (error, index) => {
                    return <Typography variant="body2">{error}</Typography>
                })

                setStateNotification({
                    isOpen: true,
                    text: errorText,
                    isError: true,
                })
            }
        }, [filesError])

        const handleDeleteFile = (name: string) => {
            const type = getFileType(name)
            const index = findIndex(files, { name: name })
            const newFileList = Array.from(files)
            newFileList.splice(index, 1)

            const indexStore = indexOf(fileUrlList, name)
            if (indexStore > -1) {
                const items = [...fileUrlList]
                items.splice(indexStore, 1)
                setFileUrlList(items)
            }

            const draftMaxFileCountByType = { ...maxFileCountByType }

            const contentType = availableExtensionsByType[type]
            const value = draftMaxFileCountByType[availableExtensionsByType[type]]

            draftMaxFileCountByType[contentType] = +value + 1

            if (has(availableExtensionsIsRequired, contentType)) {
                setAvailableExtensionsIsRequired({ [contentType]: true })
            }
            setMaxFileCountByType(draftMaxFileCountByType)
            setFile(newFileList)
            setFilesError([])
        }

        async function createRequestNewAPI(threadId: number) {
            if (currentAssistantIdDialog) {
                await connectAssistantId({ threadId }, { assistant_id: currentAssistantIdDialog })
            }

            let filesUrl
            let voice_url = null

            if (files) {
                filesUrl = await uploadFiles({ files }, threadId)
            }

            if (audioFile) {
                const [url] = await uploadFiles({ files: [audioFile] }, threadId)
                voice_url = url
            }

            const text = addQuoteInRequestText(values.text || suggestedRequest)

            await createRequestMessage(
                {
                    thread_id: threadId,
                    text,
                    image_url: null,
                    contexts: [],
                    files: filesUrl,
                    voice_url,
                },
                modelDetail?.is_free ? 0 : 1,
            )

            quoteClear()
            invalidateThreadListQuery()
            invalidateWorkplaceListQuery()
            navigate(`/request/${threadId}`)
        }

        useEffect(() => {
            const type = flattenDeep(
                filter(modelDetail?.supported_content, {
                    is_supported: true,
                })?.map((item) => {
                    return item.available_extensions
                }),
            )
            setFileTypes(type)
            setIsCanFilesInRequest(!!type?.length > 0)
        }, [modelDetail])

        useEffect(() => {
            if (errorCreateRequestMessage || errorCreateRequest) {
                setStateNotification({
                    isOpen: true,
                    text: errorCreateRequestMessage || errorCreateRequest,
                    isError: true,
                })
            }
        }, [errorCreateRequestMessage, errorCreateRequest])

        useEffect(() => {
            if (isDoneChoiceFileUrl) {
                const fileList = uniq([...files, ...fileUrlList])
                setFile(fileList)
                setChoiceFileUrl(false)
            }
        }, [isDoneChoiceFileUrl])

        useEffect(() => {
            const triggerByText =
                values.text.length > 0
                    ? values.text.length == 1 || values.text.length % 200 == '0'
                        ? true
                        : false
                    : false
            if (triggerByText) {
                const text = addQuoteInRequestText(values.text || suggestedRequest)

                fetchReplyCost({
                    voice_answer: requestSettingVoiceAnswer?.voiceAnswer ?? false,
                    thread_id: params.requestId ?? undefined,
                    model_slug: currentAgentNameInDialog,
                    reply_content: text,
                    assistant_id:
                        currentAssistantIdDialog == 'null' ? undefined : currentAssistantIdDialog,
                    web_search_used: isWebSearch,
                    rag_enabled: requestSettingRagEnabled ?? true,
                })
            }
        }, [values.text])

        useEffect(() => {
            const text = addQuoteInRequestText(values.text || suggestedRequest)
            if (
                params.requestId ||
                (isUndefined(params.requestId) && !isUndefined(currentAgentNameInDialog))
            ) {
                fetchReplyCost({
                    voice_answer: requestSettingVoiceAnswer?.voiceAnswer ?? false,
                    thread_id: params.requestId ?? undefined,
                    model_slug: currentAgentNameInDialog,
                    reply_content: text,
                    assistant_id:
                        currentAssistantIdDialog == 'null' ? undefined : currentAssistantIdDialog,
                    web_search_used: isWebSearch,
                    rag_enabled: requestSettingRagEnabled ?? true,
                })
            }
        }, [
            currentAgentNameInDialog,
            currentAssistantIdDialog,
            isWebSearch,
            requestSettingRagEnabled,
            requestSettingParams,
            requestSettingVoiceAnswer,
        ])

        useEffect(() => {
            const contentText = filter(requestByEdit?.content, {
                content_type: 'TEXT',
            })
            if (contentText[0]?.text) {
                setFieldValue('text', contentText[0]?.text)
            }
        }, [requestByEdit])

        const handleClickCancelEditText = () => {
            setRequestByEdit(undefined)
            setFieldValue('text', '')
        }

        const handleClickSubmitEditText = async () => {
            await regenerateRequestMessage(
                {
                    text: values.text,
                    thread_id: requestByEdit.thread_id,
                    regenerate: true,
                    parental_reply_id: requestByEdit.id,
                    contexts: [],
                },
                modelDetail?.is_free ? 0 : 1,
                true,
            )
            setRequestByEdit(undefined)
            setFieldValue('text', '')
        }

        const handleCreateThread = async (name: string) => {
            return await createRequest({
                workplace_id: values.workplaceId,
                agent: currentAgentNameInDialog,
                name,
                assistant_id: currentAssistantIdDialog,
                memory_message_count: requestMemoryMessageCount,
                rag_enabled: requestSettingRagEnabled ?? true,
                voice_answer: requestSettingVoiceAnswer?.voiceAnswer ?? false,
                voice_speed: requestSettingVoiceAnswer?.voiceSpeed,
                speech_voice: requestSettingVoiceAnswer?.speechVoice,
                params: requestSettingParams,
                additional_contexts: requestSettingAdditionalContexts,
                context: requestSettingContext ?? null,
                llm_tools: requestSettingLlmTools ?? null,
                deep_research_enabled: isWebSearch,
                min_balance: modelDetail?.is_free ? 0 : 1,
            })
        }

        useEffect(() => {
            if (suggestedRequest) {
                handleSubmit()
            }
        }, [suggestedRequest])

        const handleSubmitRegularRequest = () => {
            if (currentThreadId) {
                updateRegularRequestsCountInRequestDetail({ threadId: currentThreadId })
            }
            invalidateThreadListQuery()
            quoteClear()
            setRefrehAvailableParamsFiles(true)
            setFile([])
            resetForm({
                values: {
                    text: '',
                    workplaceId: workplaceId,
                    agent: currentAgentNameInDialog,
                },
            })
        }

        const handleOpenRegularRequestCreateDialog = () => {
            if (
                requestDetail &&
                requestDetail.scheduled_reply_count &&
                requestDetail.scheduled_reply_count >= REGULAR_REQUESTS_LIMIT_FOR_THREAD
            ) {
                setCreateRegularRequestError(true)
            } else {
                setRegularRequestDialogData({
                    text: addQuoteInRequestText(values.text),
                    files,
                    threadId: currentThreadId,
                })
            }
        }

        const handleCloseRegularRequestCreateDialog = () => {
            setRegularRequestDialogData(null)
        }

        const isAgentOrAssistantSelectDisabled =
            disabled ||
            (!!requestDetail?.scheduled_reply_count && requestDetail?.scheduled_reply_count > 0)

        const MobileRequestToolbar = isMobile ? (
            <Grid
                container
                sx={{
                    pb: 1,
                    display: 'flex',
                    justifyContent: 'flex-start',
                }}>
                <Grid
                    sx={{
                        display: 'flex',
                        gap: '8px',
                    }}>
                    {!currentThreadId && (
                        <ProjectSelect id={values.workplaceId} handleChange={setFieldValue} />
                    )}
                    <AgentOrAssistantSelect
                        requestId={currentThreadId}
                        isDisabled={isAgentOrAssistantSelectDisabled}
                    />
                </Grid>
            </Grid>
        ) : null

        const DesktopRequestToolbar = !isMobile ? (
            <>
                {!currentThreadId && (
                    <ProjectSelect id={values.workplaceId} handleChange={setFieldValue} />
                )}
                <AgentOrAssistantSelect
                    requestId={currentThreadId}
                    isDisabled={isAgentOrAssistantSelectDisabled}
                />
            </>
        ) : null

        const isSubmitButtonDisabled =
            isLoadingUploadFiles || isLoadingCreateRequestMessage || isRecording || isSubmitting

        return (
            <RequestInputWrapper
                isMainPage={isMainPage}
                isTempUser={isTempUser}
                ref={ref}
                isMobile={isMobile}
                stepOnboarding={stepOnboarding}>
                <OnboardingTooltip
                    placement="bottom-start"
                    open={!isMobile && stepOnboarding === 5}
                    title="Первый запрос"
                    decription="Введите запрос в строку и начните пользоваться сервисом, можете вложить файл, включить WEB-поиск, настроить, выбрать проект или ничего этого не делать">
                    <RequestBox
                        id="request-box"
                        isMainPage={isMainPage}
                        isBackground={!isMobile && stepOnboarding === 5}>
                        {helpButton}
                        {requestByEdit && (
                            <Stack
                                direction="row"
                                spacing={1}
                                p={1}
                                mb={2}
                                sx={{
                                    borderRadius: '8px',
                                    background: ' #F5F9FB',
                                    height: '36px',
                                }}>
                                <PenSquare
                                    color="#006CFD"
                                    style={{ minWidth: '16px', minHeight: '16px' }}
                                />
                                <Typography variant="body2" color="#006CFD">
                                    Редактирование:{' '}
                                    <span
                                        style={{
                                            color: '#5A5C5E',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {
                                            filter(requestByEdit?.content, {
                                                content_type: 'TEXT',
                                            })[0]?.text
                                        }
                                    </span>
                                </Typography>
                            </Stack>
                        )}

                        <form onSubmit={handleSubmit}>
                            <Grid
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    flexDirection: 'column',
                                    width: '100%',
                                    pr: helpButton ? '30px' : 0,
                                }}>
                                <Quote />
                                {!isRecording && !audioFile ? (
                                    <Grid
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            flexGrow: 1,
                                        }}>
                                        {message && (
                                            <Stack
                                                spacing={1}
                                                direction="row"
                                                useFlexGap
                                                sx={{
                                                    flexWrap: 'wrap',
                                                    background: '#F9FAFB',
                                                    borderRadius: '12px',
                                                    padding: '12px 8px',
                                                    mb: 2,
                                                }}>
                                                <AssistantLogo />
                                                <Typography variant="body2" sx={{}}>
                                                    {message}
                                                </Typography>
                                            </Stack>
                                        )}

                                        <StyledInput
                                            disabled={disabled || stepOnboarding === 5}
                                            autoFocus={isMobile ? false : true}
                                            fullWidth
                                            maxRows={8}
                                            multiline
                                            placeholder={
                                                !isMobile
                                                    ? 'Спроси что угодно у Arhiassistant..'
                                                    : 'Спроси что угодно'
                                            }
                                            variant="outlined"
                                            id="text"
                                            name="text"
                                            value={values.text}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            onKeyDown={(e) => {
                                                if (e.target.value.trim().length >= 1) {
                                                    if (
                                                        !requestByEdit &&
                                                        e.key == 'Enter' &&
                                                        !e.shiftKey &&
                                                        !isMobile
                                                    ) {
                                                        e.preventDefault()
                                                        handleSubmit()
                                                    }
                                                }
                                            }}
                                            InputProps={{
                                                sx: {
                                                    border: 'none !important',
                                                },
                                            }}
                                        />
                                    </Grid>
                                ) : null}

                                <Grid
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding:
                                            audioFile || isRecording
                                                ? `12px 0 ${isRecording ? '47px' : '14px'}`
                                                : '',
                                    }}>
                                    {audioFile ? (
                                        <IconButton
                                            size="large"
                                            sx={{
                                                backgroundColor: (theme) =>
                                                    theme.palette.primary.main,
                                            }}
                                            disabled={
                                                isLoadingUploadFiles ||
                                                isLoadingCreateRequestMessage ||
                                                isRecording
                                            }
                                            onClick={() => wavesurferRef.current?.playPause()}>
                                            {isPlaying ? <PauseIcon /> : <PlayIcon />}
                                        </IconButton>
                                    ) : null}
                                    <Grid
                                        sx={{
                                            flexGrow: 1,
                                        }}
                                        ref={waveformRef}
                                    />
                                    {audioFile ? (
                                        <IconButton
                                            size="large"
                                            disabled={
                                                isLoadingUploadFiles ||
                                                isLoadingCreateRequestMessage ||
                                                isRecording
                                            }
                                            onClick={() => {
                                                wavesurferRef.current?.empty()
                                                setAudioFile(null)
                                                setIsPlaying(false)
                                            }}>
                                            <TrashIcon color="#EF4848" />
                                        </IconButton>
                                    ) : null}
                                </Grid>
                            </Grid>
                            {!isRecording && (
                                <>
                                    {files?.length > 0 && (
                                        <FilesWrapper container>
                                            {map(files, (file, index) => {
                                                return (
                                                    <DocumentIconName
                                                        key={index}
                                                        name={file.name ?? file}>
                                                        <IconButton
                                                            size="large"
                                                            aria-label="delete"
                                                            color="primary"
                                                            onClick={() =>
                                                                handleDeleteFile(file.name ?? file)
                                                            }>
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </DocumentIconName>
                                                )
                                            })}
                                        </FilesWrapper>
                                    )}
                                    <Grid
                                        container
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                        }}>
                                        <Grid
                                            sx={{
                                                display: 'flex',
                                                gap: '8px',
                                            }}>
                                            <RequestSettingButton isDisabled={disabled} />

                                            {fileTypes.length > 0 && (
                                                <FilesMenu
                                                    isDisabled={disabled}
                                                    fileTypes={fileTypes}
                                                    setFilesError={setFilesError}
                                                    files={files}
                                                    handleChangeFiles={handleChangeFiles}
                                                    setOpenFileListDialog={setOpenFileListDialog}
                                                />
                                            )}
                                            {!isProtectedCurrentProject && (
                                                <WebSearchButton
                                                    isDisabled={!isFullAccess || disabled}
                                                />
                                            )}

                                            {DesktopRequestToolbar}
                                        </Grid>

                                        {!audioFile && !isRecording && !values.text.trim() ? (
                                            <Tooltip title="Запись и отправка запроса голосом">
                                                <IconButton
                                                    disabled={!isFullAccess || disabled}
                                                    size="large"
                                                    onClick={() => {
                                                        startRecording()
                                                    }}
                                                    sx={{
                                                        backgroundColor: '#F9FAFB',
                                                        marginLeft: 'auto',
                                                    }}>
                                                    <MicrophoneIcon />
                                                </IconButton>
                                            </Tooltip>
                                        ) : requestByEdit ? (
                                            ''
                                        ) : (
                                            <SubmitButton
                                                handleOpenRegularRequestCreateDialog={
                                                    handleOpenRegularRequestCreateDialog
                                                }
                                                isTempUser={!!isTempUser}
                                                hasAudio={!!audioFile}
                                                disabled={isSubmitButtonDisabled}
                                            />
                                        )}
                                        {requestByEdit && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                mt={isMobile ? 2 : 0}>
                                                <Button
                                                    onClick={handleClickCancelEditText}
                                                    variant="outlined">
                                                    Отменить
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleClickSubmitEditText}>
                                                    Подтвердить
                                                </Button>
                                            </Stack>
                                        )}
                                    </Grid>
                                </>
                            )}

                            {isRecording ? (
                                <Grid
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '100%',
                                        gap: '30px',
                                    }}>
                                    <Grid
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}>
                                        {!isRecording ? (
                                            <IconButton
                                                size="large"
                                                disabled={disabled}
                                                onClick={startRecording}
                                                sx={{
                                                    backgroundColor: (theme) =>
                                                        theme.palette.primary.main,
                                                    marginLeft: 'auto',
                                                }}>
                                                <MicrophoneIcon />
                                            </IconButton>
                                        ) : (
                                            <>
                                                <Grid
                                                    sx={{
                                                        display: 'flex',
                                                        gap: '6px',
                                                        alignItems: 'center',
                                                    }}>
                                                    <Box
                                                        sx={{
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '8px',
                                                            backgroundColor: (theme) =>
                                                                theme.palette.error.main,
                                                        }}
                                                    />
                                                    {formatTime(recordTime)}
                                                </Grid>
                                                <IconButton
                                                    size="large"
                                                    onClick={() => {
                                                        stopRecording()
                                                    }}
                                                    sx={{
                                                        backgroundColor: (theme) =>
                                                            theme.palette.error.main,
                                                    }}>
                                                    <SquareIcon color="#fff" />
                                                </IconButton>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            ) : null}
                        </form>
                    </RequestBox>
                </OnboardingTooltip>
                {MobileRequestToolbar}
                {openFileListDialog && (
                    <FileListDialog
                        open={openFileListDialog}
                        handleClose={handleCloseFileListDialog}
                        projectId={values.workplaceId}
                    />
                )}
                {assistantPresets && (
                    <Stack
                        spacing={1}
                        direction="row"
                        useFlexGap
                        sx={{ flexWrap: 'wrap', justifyContent: 'center', mb: 1 }}>
                        {map(assistantPresets, (item) => {
                            return (
                                <Button
                                    onClick={() => setFieldValue('text', item)}
                                    color="inherit"
                                    sx={{
                                        color: '#1B1C1D',
                                        border: '1px solid #E9EAED',
                                        background: '#F9FAFB',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontSize: isMobile ? '10px' : '12px',
                                        wordBreak: 'break-word',
                                        whiteSpace: 'pre-wrap',
                                    }}
                                    variant="contained">
                                    {item}
                                </Button>
                            )
                        })}
                    </Stack>
                )}
                <RegularRequestCreateDialog
                    open={!!regularRequestDialogData}
                    initialData={regularRequestDialogData}
                    onClose={handleCloseRegularRequestCreateDialog}
                    handleCreateThread={handleCreateThread}
                    onSubmit={handleSubmitRegularRequest}
                />
                <Notistack
                    isError
                    open={createRegularRequestError}
                    message="Превышен лимит запросов по расписанию для одного диалога"
                    onClose={() => setCreateRegularRequestError(false)}
                />
                <ReplyCost replyCost={replyCost} />
            </RequestInputWrapper>
        )
    },
)
